'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useLayoutEffect, useSyncExternalStore } from 'react';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { icon } from '@/uiPrimitives/media/icon';
import { hiddenAccessible } from '@/uiPrimitives/utility/display.css';
import { moonIcon } from '@/uiIcons/moonIcon.css';
import { sunIcon } from '@/uiIcons/sunIcon.css';
import { horizontalToggle, iconDarkColorScheme, iconLightColorScheme, iconSize, toggleTrack, toggleThumb, toggleTrackInner, verticalToggle, disableTransitions } from './ColorSchemeToggle.css';
import { translateInline } from '@/globals/translate/translateInline';
import { COLOR_SCHEMES, ColorScheme, colorSchemeDataName, colorSchemeCookieName, colorSchemeAttributeName } from '../../uiPrimitives/color/colorScheme.types';
import { pile } from '@/uiPrimitives/layout/pile';
import { shelf } from '@/uiPrimitives/layout/shelf';
declare global {
  interface _dop {
    setColorSchemeAttribute?: () => void;
  }
  interface Window {
    dop?: _dop;
  }
}
const validateColorScheme = (value: string | undefined): ColorScheme | undefined => {
  return COLOR_SCHEMES.includes(value as ColorScheme) ? value as ColorScheme : undefined;
};
const subscribe = (callback: () => void) => {
  const abortController = new AbortController();
  const observer = new MutationObserver(() => {
    callback();
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: [colorSchemeAttributeName]
  });
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', callback, {
    signal: abortController.signal
  });
  return () => {
    observer.disconnect();
    abortController.abort();
  };
};
const setColorSchemeCookie = (colorScheme: string) => {
  window.cookieStore.set({
    name: colorSchemeCookieName,
    value: colorScheme,
    expires: Date.now() + 365 * 24 * 60 * 60 * 1000,
    // 1 year
    sameSite: 'lax'
  });
};
const getColorSchemeFromAttribute = () => validateColorScheme(document.documentElement.dataset[colorSchemeDataName]);
const getSnapshot = () => {
  const colorSchemeFromAttribute = getColorSchemeFromAttribute();
  if (colorSchemeFromAttribute != null) return colorSchemeFromAttribute;
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};
const getServerSnapshot = () => undefined;
export const ColorSchemeToggle = t0 => {
  const $ = _c(19);
  const {
    className,
    vertical: t1
  } = t0;
  const vertical = t1 === undefined ? false : t1;
  const id = useId();
  const labelId = `label-${id}`;
  const colorScheme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useLayoutEffect(_temp, t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  useEffect(_temp2, t3);
  const t4 = vertical ? verticalToggle : horizontalToggle;
  let t5;
  if ($[2] !== className || $[3] !== t4) {
    t5 = classes(toggleTrack, className, t4);
    $[2] = className;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== colorScheme) {
    t6 = async () => {
      const newColorScheme = colorScheme === "dark" ? "light" : "dark";
      document.documentElement.classList.add(disableTransitions);
      document.documentElement.dataset[colorSchemeDataName] = newColorScheme;
      await new Promise(_temp3);
      document.documentElement.classList.remove(disableTransitions);
      setColorSchemeCookie(newColorScheme);
    };
    $[5] = colorScheme;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== colorScheme) {
    t7 = colorScheme === "dark" ? translateInline({
      en: "Disable dark color scheme",
      nl: "Donker kleurenschema uitschakelen"
    }) : translateInline({
      en: "Enable dark color scheme",
      nl: "Donker kleurenschema inschakelen"
    });
    $[7] = colorScheme;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== labelId || $[10] !== t7) {
    t8 = <span id={labelId} className={hiddenAccessible}>{t7}</span>;
    $[9] = labelId;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className={classes(toggleTrackInner, pile({
      justifyItems: "stretch",
      alignItems: "center"
    }))}><i className={toggleThumb} /><span className={shelf({
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        alignItems: "center",
        justifyItems: "center"
      })}><i className={classes(icon({
          icon: sunIcon,
          size: iconSize
        }), iconLightColorScheme)} /><i className={classes(icon({
          icon: moonIcon,
          size: iconSize
        }), iconDarkColorScheme)} /></span></span>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== id || $[14] !== labelId || $[15] !== t5 || $[16] !== t6 || $[17] !== t8) {
    t10 = <button id={id} type="button" className={t5} aria-labelledby={labelId} onClick={t6}>{t8}{t9}</button>;
    $[13] = id;
    $[14] = labelId;
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
};
function _temp() {
  window.dop?.setColorSchemeAttribute?.();
}
function _temp2() {
  const colorScheme_0 = getColorSchemeFromAttribute();
  if (colorScheme_0 != null) {
    setColorSchemeCookie(colorScheme_0);
  }
}
function _temp3(resolve) {
  return requestAnimationFrame(resolve);
}